import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import ProjectSettingToggle from "./ProjectSettingToggle";
import dayjs from "dayjs";
import { toUTCMidnight } from "../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createRFI, fetchNextRFINumber } from "../services/rfisAPI";
import { fetchProjectUsers } from "../services/projectsAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import { handleIntegerKeyDown, createIntegerChangeHandler } from "../utils/textUtils";
import AttachmentsSection from "./AttachmentsSection";
import { fetchTemplates, createTemplate, updateTemplate } from "../services/templatesAPI";
import TemplateModal from "./TemplateModal";
import { MinusCircleOutlined, PlusOutlined, EditOutlined, FileTextOutlined } from "@ant-design/icons";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import ProjectUserSelect from "./ProjectUserSelect";
import SpecSectionSelect from "./SpecSectionSelect";

const CreateRFIModal = ({ visible, onClose, initialData = null, onCreated }) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const [form] = Form.useForm();
  const [sendEmail, setSendEmail] = useState(true);
  const isSequentialWorkflow = selectedProject?.rfi_workflow_type === "Sequential";
  const statusValue = Form.useWatch("status", form);
  const emailNotificationsDisabled = statusValue ? statusValue !== "Open" : false;
  
  // Template states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [ccSearchValue, setCcSearchValue] = useState("");
  const [assigneeSearchValue, setAssigneeSearchValue] = useState("");

  const { data: projectUsers } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ["templates", projectId, "rfi"],
    queryFn: () => fetchTemplates(projectId, "rfi"),
    enabled: !!projectId,
  });

  // Sync form field with selectedTemplate state
  useEffect(() => {
    if (selectedTemplate) {
      form.setFieldsValue({ template_id: selectedTemplate._id });
    } else {
      form.setFieldsValue({ template_id: null });
    }
  }, [selectedTemplate, form]);

  useEffect(() => {
    if (visible && projectId) {
      fetchNextRFINumber(projectId).then((data) => {
        form.resetFields();
        form.setFieldsValue({
          number: data.next_number,
          rfi_manager_id: currentUser.id,
          status: "Open",
          ...(initialData || {}),
        });
      });
    }
  }, [visible, projectId, form, currentUser.id, initialData]);

  useEffect(() => {
    if (statusValue && emailNotificationsDisabled) {
      setSendEmail(false);
      form.setFieldsValue({ email_notifications: false });
    }
  }, [emailNotificationsDisabled, form, statusValue]);

  const mutation = useMutation({
    mutationFn: ({ rfiData, sendEmail }) => createRFI(rfiData, sendEmail),
    onSuccess: (createdRfi) => {
      queryClient.invalidateQueries(["rfis", projectId]);
      queryClient.invalidateQueries(["rfis-simple", projectId]);
      onCreated?.(createdRfi);
      onClose();
      form.resetFields();
      setSendEmail(true);
      setSelectedTemplate(null);
      setCcSearchValue("");
      setAssigneeSearchValue("");
      notification.success({ message: "RFI created successfully" });
    },
    onError: (error) => {
      const data = error.response?.data;
      const detail = data?.detail;
      const description = detail?.message || detail || error.message;
      notification.error({
        message: "Failed to create RFI",
        description,
      });
    },
  });

  const handleReceivedFromChange = (value) => {
    if (value) {
      const receivedFrom = projectUsers?.data?.find(
        (user) => user.id === value || user?._id === value
      );
      form.setFieldsValue({
        responsible_contractor_id: receivedFrom?.vendor_id,
      });
    } else {
      form.setFieldsValue({
        responsible_contractor_id: undefined,
      });
    }
  };

  const handleCreateRFI = async (values) => {
    const rfiData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || [],
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
    };

    // Handle Review Order for sequential workflow
    if (isSequentialWorkflow && values.steps && values.steps.length > 0) {
      rfiData.steps = values.steps.map((step) => step.user_ids || []);
      rfiData.assignee_ids = []; // Clear assignee_ids for sequential workflow
    } else {
      rfiData.steps = [];
      rfiData.ball_in_court_ids = values.assignee_ids || [];
    }

    mutation.mutate({ rfiData, sendEmail });
  };

  const handleClose = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, Close",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          form.resetFields();
          setSendEmail(true);
          setSelectedTemplate(null);
          setCcSearchValue("");
          setAssigneeSearchValue("");
          onClose();
        },
      });
    } else {
      form.resetFields();
      setSendEmail(true);
      setSelectedTemplate(null);
      setCcSearchValue("");
      setAssigneeSearchValue("");
      onClose();
    }
  };

  const handleTemplateChange = async (templateId) => {
    if (templateId) {
      const template = templates?.templates?.find(t => t._id === templateId);
      setSelectedTemplate(template);
      
      // Check if form has existing data
      const formValues = form.getFieldsValue();
      const hasExistingData = !!(
        formValues.description || 
        formValues.question ||
        formValues.received_from_id ||
        formValues.responsible_contractor_id ||
        formValues.spec_section_id ||
        formValues.rfi_manager_id ||
        (formValues.distribution_list && formValues.distribution_list.length > 0) ||
        (formValues.attachments && formValues.attachments.length > 0)
      );
      
      const updateTemplateData = async () => {
        // Calculate due date based on template duration
        const currentDate = new Date();
        const dueDate = new Date(
          currentDate.getTime() + template.days_duration * 24 * 60 * 60 * 1000
        );

        // Update due_date and distribution_list from template
        const setValues = {
          template_id: templateId,
          due_date: dayjs(dueDate),
          distribution_list: template.distribution_list || [],
        };

        // For RFI: populate steps or assignees based on workflow
        if (isSequentialWorkflow) {
          if (template?.steps && template.steps.length > 0) {
            setValues.steps = template.steps.map((step) => ({
              user_ids: (step.reviews || []).map((r) => r.reviewer_id),
            }));
          }
          setValues.assignee_ids = [];
        } else {
          // parallel assignee_ids from template
          if (template?.assignee_ids && template.assignee_ids.length > 0) {
            setValues.assignee_ids = template.assignee_ids;
          }
          setValues.steps = [];
        }

        form.setFieldsValue(setValues);
      };
      
      if (hasExistingData) {
        // Show confirmation modal
        Modal.confirm({
          title: 'Are you sure?',
          content: 'There is existing data in the form. Are you sure you want to update the due date and distribution list with template data?',
          okText: 'Yes',
          cancelText: 'No',
          onOk: updateTemplateData,
          onCancel: () => {
            // Reset template selection if user cancels
            form.setFieldsValue({ template_id: null });
            setSelectedTemplate(null);
          }
        });
      } else {
        // No existing data, update directly
        await updateTemplateData();
      }
    } else {
      setSelectedTemplate(null);
    }
  };

  useEffect(() => {
    setSelectedTemplate(null);
    form.setFieldsValue({ template_id: null });
  }, [projectId, form]);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsTemplateModalVisible(true);
  };

  const handleEditTemplate = () => {
    if (selectedTemplate) {
      setEditingTemplate(selectedTemplate);
      setIsTemplateModalVisible(true);
    }
  };

  const handleTemplateModalClose = () => {
    setIsTemplateModalVisible(false);
    setEditingTemplate(null);
  };

  const handleTemplateDeleted = () => {
    setSelectedTemplate(null);
  };

  const handleTemplateSave = async (templateData) => {
    try {
      if (editingTemplate) {
        const updatedTemplate = await updateTemplate(editingTemplate._id, templateData);
        notification.success({ message: "Template updated successfully" });
        
        // Update selectedTemplate with the updated data
        setSelectedTemplate(updatedTemplate);
        
        // Update form with updated template data
        const currentDate = new Date();
        const dueDate = new Date(currentDate.getTime() + (updatedTemplate.days_duration * 24 * 60 * 60 * 1000));
        
        const setValues = {
          template_id: updatedTemplate._id,
          due_date: dayjs(dueDate),
          distribution_list: updatedTemplate.distribution_list || [],
        };
        if (isSequentialWorkflow) {
          if (updatedTemplate?.steps && updatedTemplate.steps.length > 0) {
            setValues.steps = updatedTemplate.steps.map((step) => ({
              user_ids: (step.reviews || []).map((r) => r.reviewer_id),
            }));
          } else {
            setValues.steps = [];
          }
          setValues.assignee_ids = [];
        } else {
          setValues.assignee_ids = updatedTemplate?.assignee_ids || [];
          setValues.steps = [];
        }

        form.setFieldsValue(setValues);
      } else {
        await createTemplate({
          ...templateData,
          project_id: projectId,
          type: "rfi",
        });
        notification.success({ message: "Template created successfully" });
      }
      
      // Invalidate templates query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["templates", projectId, "rfi"]
      });
      
      setIsTemplateModalVisible(false);
      setEditingTemplate(null);
    } catch (error) {
      const errorMessage = error?.response?.data?.detail?.message || error?.message || "Failed to save template";
      notification.error({
        message: "Failed to save template",
        description: errorMessage,
      });
    }
  };

  return (
    <Modal
      title="Create RFI"
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      {/* Template Selection */}
      <div className="mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <FileTextOutlined className="text-xs" />
            Template:
          </span>
          <Select
            placeholder="Select template..."
            value={selectedTemplate?._id}
            onChange={handleTemplateChange}
            allowClear
            loading={templatesLoading}
            options={templates?.templates?.map((template) => ({
              value: template._id,
              label: template.name,
            }))}
            className="flex-1"
            size="small"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={handleCreateTemplate}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Add Template"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={handleEditTemplate}
            disabled={!selectedTemplate || !selectedTemplate._id}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Edit Template"
          />
        </div>
      </div>

      <Form form={form} onFinish={handleCreateRFI} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Number"
              name="number"
              rules={[
                { required: true, message: "Please input the number!" },
                {
                  pattern: /^\d+$/,
                  message: "Please enter only whole numbers!",
                },
              ]}
            >
              <Input
                type="number"
                step="1"
                onKeyDown={handleIntegerKeyDown}
                onChange={createIntegerChangeHandler(form, "number")}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Status"
              name="status"
              className="w-full"
              initialValue="Open"
            >
              <Select
                options={[
                  { value: "Open", label: "Open" },
                  { value: "Draft", label: "Draft" },
                  { value: "Closed", label: "Closed" },
                ]}
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please input the title!" }]}
          className="w-full"
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Question"
          name="question"
          rules={[{ required: true, message: "Please input the question!" }]}
          className="w-full"
        >
          <SimpleRichTextEditor
            placeholder="Enter your question..."
            aiEnhanceTarget="question"
          />
        </Form.Item>

        {!isSequentialWorkflow && (
          <Form.Item
            label="Assignees"
            name="assignee_ids"
            className="w-full"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(
                      new Error("Please select at least one assignee!")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ProjectUserSelect
              searchValue={assigneeSearchValue}
              onSearch={setAssigneeSearchValue}
              placeholder="Select at least one assignee"
              className="w-full"
            />
          </Form.Item>
        )}

        {isSequentialWorkflow && (
          <Form.Item
            label="Review Order"
            name="steps"
            className="w-full"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(
                      new Error("Please add at least one Review Order!")
                    );
                  }
                  // Check that each step has at least one reviewer
                  for (let i = 0; i < value.length; i++) {
                    if (!value[i]?.user_ids || value[i].user_ids.length === 0) {
                      return Promise.reject(
                        new Error(
                          `Step #${i + 1} must have at least one reviewer!`
                        )
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Form.List name="steps">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey }, index) => (
                    <div key={key} className="mb-4 flex items-center">
                      <Form.Item
                        label={`Step #${index + 1}`}
                        name={[name, "user_ids"]}
                        fieldKey={[fieldKey, "user_ids"]}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value || value.length === 0) {
                                return Promise.reject(
                                  new Error(
                                    "Please select at least one reviewer!"
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        className="w-full"
                      >
                        <ProjectUserSelect
                          placeholder="Select at least one reviewer"
                          className="flex-grow"
                        />
                      </Form.Item>
                      <Button
                        type="danger"
                        onClick={() => remove(name)}
                        icon={<MinusCircleOutlined />}
                        className="ml-2"
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    className="mb-4"
                  >
                    Add Step
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="RFI Manager"
              name="rfi_manager_id"
              rules={[
                { required: true, message: "Please select the RFI manager!" },
              ]}
              className="w-full"
            >
              <ProjectUserSelect
                mode={null}
                placeholder="Select RFI manager"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Due Date" name="due_date" className="w-full">
              <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Received From"
              name="received_from_id"
              className="w-full"
              getValueFromEvent={(value) => {
                handleReceivedFromChange(value);
                return value;
              }}
            >
              <ProjectUserSelect
                mode={null}
                allowClear
                placeholder="Select received from"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Specification Section"
              name="spec_section_id"
              className="w-full"
            >
              <SpecSectionSelect className="w-full" labelSeparator=" - " />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="responsible_contractor_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item label="Cc" name="distribution_list" className="w-full">
          <ProjectUserSelect
            searchValue={ccSearchValue}
            onSearch={setCcSearchValue}
            placeholder="Select Cc"
            className="w-full"
            allowClear
          />
        </Form.Item>
        <Form.Item name="attachments" label="Attachments" className="w-full">
          <AttachmentsSection projectId={projectId} />
        </Form.Item>
        <div className="project-settings-grid">
          <ProjectSettingToggle
            name="email_notifications"
            title="Email Notifications"
            description={
              emailNotificationsDisabled
                ? "Set status to Open to enable emails"
                : undefined
            }
            initialValue={true}
            checked={sendEmail}
            disabled={emailNotificationsDisabled}
            onChange={(checked) => {
              setSendEmail(checked);
              form.setFieldsValue({ email_notifications: checked });
            }}
          />
        </div>
        <Form.Item className="w-full">
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={mutation.isPending}
            className="w-full mt-4"
          >
            {sendEmail ? "Create and Send Emails" : "Create RFI"}
          </Button>
        </Form.Item>
      </Form>

      {/* Template Modal */}
      <TemplateModal
        visible={isTemplateModalVisible}
        onClose={handleTemplateModalClose}
        onSave={handleTemplateSave}
        editingTemplate={editingTemplate}
        templateType="rfi"
        onTemplateDeleted={handleTemplateDeleted}
      />
    </Modal>
  );
};

export default CreateRFIModal;
